import React, { useEffect, useMemo, useState } from "react";
import {
  Form,
  Input,
  Row,
  Col,
  DatePicker,
  Button,
  message,
  Modal,
  Select,
} from "antd";
import { EnvironmentOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import config from "../../../config";
import SingleImageUploader from "../../shared/imageUploaders/singleImage/SingleImageUploader";
import axios from "axios";
import moment from "moment";

// Components
import PoliticalParty from "./components/PoliticalParty";
import Religion from "./components/Religion";
import Position from "./components/Position";
import OfficeSelectionReformed from "./components/office/OfficeSelectionReformed";
import Vote from "./components/Vote";
import Contribution from "./components/Contribution";
import Committee from "./components/Committee";
import FamilyNew from "./components/FamilyNew";
import Education from "./components/Education";
import SocialMedia from "./components/SocialMedia";
import OfficeContacts from "./components/OfficeContacts";
import CustomInput from "../../../components/shared/onboarding/CustomInput";
import ProfessionalExperience from "./components/ProfessionalExperience";

// --------- localStorage safe helpers (prevents random blank screens) ----------
const safeJSONParse = (value, fallback = null) => {
  try {
    if (!value || value === "undefined" || value === "null") return fallback;
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const safeGetItem = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    console.warn(`localStorage.getItem failed for key="${key}"`, e);
    return null;
  }
};

const safeSetJSON = (key, obj) => {
  try {
    localStorage.setItem(key, JSON.stringify(obj));
    return true;
  } catch (e) {
    console.warn(`localStorage.setItem(JSON) failed for key="${key}"`, e);
    return false;
  }
};

// --------- Google Place Autocomplete (guarded) ----------
let cityAutoComplete;
let addressAutoComplete;

function handleScriptLoad(
  setCityQuery,
  cityId,
  setOfficeAddressQuery,
  addressId
) {
  // ✅ Guard: if Google script is blocked/not loaded, do nothing
  if (!window.google?.maps?.places?.Autocomplete) return;
  if (!cityId || !addressId) return;

  cityAutoComplete = new window.google.maps.places.Autocomplete(cityId, {
    types: ["(cities)"],
    componentRestrictions: { country: "us" },
  });
  cityAutoComplete.setFields(["address_components", "formatted_address"]);
  cityAutoComplete.addListener("place_changed", () =>
    handleCityPlaceSelect(setCityQuery)
  );

  addressAutoComplete = new window.google.maps.places.Autocomplete(addressId, {
    types: ["address"],
    componentRestrictions: { country: "us" },
  });
  addressAutoComplete.setFields(["address_components", "formatted_address"]);
  addressAutoComplete.addListener("place_changed", () =>
    handleAddressPlaceSelect(setOfficeAddressQuery)
  );
}

async function handleCityPlaceSelect(updateQuery) {
  try {
    const cityObject = cityAutoComplete?.getPlace?.();
    const query = cityObject?.formatted_address || "";
    updateQuery(query);
  } catch {
    // ignore
  }
}

async function handleAddressPlaceSelect(updateQuery) {
  try {
    const addressObject = addressAutoComplete?.getPlace?.();
    const query = addressObject?.formatted_address || "";
    updateQuery(query);
  } catch {
    // ignore
  }
}

const EditCandidateForm = (props) => {
  // ✅ IMPORTANT: create `form` before loadInitialData uses it
  const [form] = Form.useForm();

  const [formValues, setFormValues] = useState();
  const [disableSubmitBtn, setDisableSubmitBtn] = useState(true);
  const [initialValues, setInitialValues] = useState({});

  // Profile Picture
  const [logoUrl, setLogoUrl] = useState(
    props.initialData ? props.initialData.bio.profileImage : ""
  );

  // Home City & Office Address
  const [cityQuery, setCityQuery] = useState(
    props?.initialData?.bio?.personal?.homeCity
      ? props.initialData.bio.personal.homeCity
      : ""
  );

  const [officeAddressQuery, setOfficeAddressQuery] = useState(
    props?.initialData?.bio?.contact?.address
      ? props.initialData.bio.contact.address
      : ""
  );

  // Office Selection
  const [divisionIds, setDivisionIds] = useState({
    incumbent: undefined,
    ballot: undefined,
  });

  // Birthdate
  const dateFormatList = ["DD/MM/YYYY", "DD/MM/YY"];

  // Profile Type
  const [profileType, setProfileType] = useState("");

  // Navigation - Cancel Button
  const history = useHistory();
  const backToAccountTypesOptions = () => {
    props.cancelModal(false);
  };

  // loading
  const [loading, setLoading] = useState(false);

  // ✅ safer user read (won't crash if localStorage is corrupted)
  const storedUser = useMemo(
    () => safeJSONParse(safeGetItem("user"), {}), // ✅ Safe nested call
    []
  );

  useEffect(() => {
    // Guarded autocomplete init
    handleScriptLoad(
      setCityQuery,
      document.getElementById("city-place-autocomplete"),
      setOfficeAddressQuery,
      document.getElementById("address-place-autocomplete")
    );

    loadInitialData();

    // eslint-disable-next-line
  }, []);

  const loadInitialData = async () => {
    if (!props.initialData) return;

    try {
      const init = props.initialData;

      if (init?.bio?.name) {
        form.setFieldsValue({ name: init.bio.name });
      }

      if (init?.bio?.personal?.birthdate) {
        const birthDate = moment(init.bio.personal.birthdate, "YYYY-MM-DD");
        form.setFieldsValue({ birthdate: birthDate });
      }

      if (init?.bio?.personal?.contact) {
        form.setFieldsValue({ contact: init.bio.personal.contact });
      }

      // profile type
      if (init?.bio?.office?.profileType) {
        form.setFieldsValue({ profileType: init.bio.office.profileType });
      }

      if (init?.bio?.office?.party) {
        form.setFieldsValue({ politicalParty: init.bio.office.party });
      }

      if (init?.bio?.contact?.phone) form.setFieldsValue({ phone: init.bio.contact.phone });
      if (init?.bio?.contact?.email) form.setFieldsValue({ email: init.bio.contact.email });
      if (init?.bio?.contact?.fax) form.setFieldsValue({ fax: init.bio.contact.fax });

      if (init?.bio?.personal?.religion) {
        form.setFieldsValue({ religion: init.bio.personal.religion });
      }

      // family
      if (init?.bio?.personal?.family) {
        const family = init.bio.personal.family;

        if (Array.isArray(family.spouses)) {
          const spouses = family.spouses.map((s) => ({
            spouse: s?.Name || "",
            noOfChildren: s?.childrens,
          }));
          form.setFieldsValue({ spouses });
        }

        form.setFieldsValue({
          father: family.father,
          mother: family.mother,
        });
      }

      if (init?.bio?.personal?.education) {
        form.setFieldsValue({
          college: init.bio.personal.education.college,
          highschool: init.bio.personal.education.highschool,
        });
      }

      if (init?.bio?.other?.experience) {
        form.setFieldsValue({ otherMembership: init.bio.other.experience });
      }

      // positions
      if (Array.isArray(init.positions)) {
        const positions = init.positions.map((p) => ({
          category: p.topic,
          detail: p.position,
        }));
        form.setFieldsValue({ positions });
      }

      // legislations
      if (Array.isArray(init.legislations)) {
        const legislations = init.legislations.map((l) => ({
          bill: l.bill,
          billName: l.billName,
          sponsor: l.sponsor,
          summary: l.summary,
          vote: l.vote,
        }));
        form.setFieldsValue({ legislations });
      }

      // contributions
      if (Array.isArray(init.contributions)) {
        const contributions = init.contributions.map((c) => ({
          industry: c.industry,
          name: c.name,
          amount: c.amount,
          yearOfContribution: c.yearOfContribution,
        }));
        form.setFieldsValue({ contributions });
      }

      // officeSelections
      if (Array.isArray(init.officeSelections)) {
        const officeSelections = init.officeSelections.map((o) => ({
          politicalDivision: o.politicalDivision,
          title: o.title,
          position: o.position,
          district: o.district,
          state: o.state,
          division: o.city,
          office: o.office,
          term: o.term,
        }));
        form.setFieldsValue({ officeSelections });
      }

      // professionalExperiences
      if (Array.isArray(init.professionalExperiences)) {
        const professionalExperiences = init.professionalExperiences.map((p) => ({
          title: p.title,
          startDate: p.startDate ? moment(p.startDate, "YYYY-MM-DD") : null,
          endDate: p.endDate ? moment(p.endDate, "YYYY-MM-DD") : null,
        }));
        form.setFieldsValue({ professionalExperiences });
      }

      // committees
      if (Array.isArray(init.committees)) {
        const committees = init.committees.map((c) => ({
          committeeDate: c.date ? moment(c.date, "YYYY-MM-DD") : null,
          description: c.description,
        }));
        form.setFieldsValue({ committees });
      }

      // socials
      const social = init?.bio?.social || {};
      if (social.website) form.setFieldsValue({ website: social.website });
      if (social.facebook) form.setFieldsValue({ facebook: social.facebook });
      if (social.instagram) form.setFieldsValue({ instagram: social.instagram });
      if (social.twitter) form.setFieldsValue({ twitter: social.twitter });
      if (social.youtube) form.setFieldsValue({ youtube: social.youtube });

      // ✅ store initial values after setFieldsValue calls finish
      const current = form.getFieldsValue(true);
      setInitialValues(current);
      setDisableSubmitBtn(true);
    } catch (err) {
      console.error(err);
      message.error("There was an error loading candidate data");
    }
  };

  // Submission
  const onFinish = (values) => {
    if (officeAddressQuery) values.address = officeAddressQuery;
    if (cityQuery) values.homeCity = cityQuery;

    const init = props.initialData;

    const data = {
      bio: {
        rating: {
          total: init?.bio?.rating?.total,
          stars: init?.bio?.rating?.stars,
        },
        profileImage: logoUrl,
        name: values.name,
        social: {
          website: values.website,
          facebook: values.facebook,
          twitter: values.twitter,
          instagram: values.instagram,
          youtube: values.youtube,
          podcasts: values.podcasts,
        },
        general: {
          comments: init?.bio?.general?.comments || [],
        },
        office: {
          party: values.politicalParty,
          profileType: values.profileType,
        },
        contact: {
          phone: values.phone,
          fax: values.fax,
          address: values.address,
          email: values.email,
        },
        personal: {
          birthdate: values.birthdate,
          contact: values.contact,
          homeCity: values.homeCity,
          family: {
            spouses: [],
            childrens: values.childrens,
            father: values.father,
            mother: values.mother,
          },
          education: {
            college: values.college,
            highschool: values.highschool,
          },
          religion: values.religion,
          comments: init?.bio?.personal?.comments || [],
        },
        other: {
          experience: values.otherMembership,
          comments: init?.bio?.other?.comments || [],
        },
      },
      positions: [],
      legislations: [],
      contributions: [],
      committees: [],
      officeSelections: [],
      professionalExperiences: [],
      questions: init?.questions || [],
    };

    if (values.positions?.[0]) {
      values.positions.forEach((position) => {
        data.positions.push({
          topic: position.category,
          position: position.detail,
          comments: [],
        });
      });
    }

    if (values.legislations?.[0]) {
      values.legislations.forEach((legislation) => {
        data.legislations.push({
          bill: legislation.bill,
          billName: legislation.billName,
          sponsor: legislation.sponsor,
          summary: legislation.summary,
          vote: legislation.vote,
        });
      });
    }

    if (values.contributions?.[0]) {
      values.contributions.forEach((contribution) => {
        data.contributions.push({
          industry: contribution.industry,
          name: contribution.name,
          amount: contribution.amount,
          yearOfContribution: contribution.yearOfContribution,
        });
      });
    }

    if (values.committees?.[0]) {
      values.committees.forEach((committee) => {
        data.committees.push({
          // NOTE: your form uses `committeeDate`, not `date`
          date: committee.committeeDate,
          description: committee.description,
        });
      });
    }

    if (values.officeSelections?.[0]) {
      values.officeSelections.forEach((officeSelection) => {
        data.officeSelections.push({
          politicalDivision: officeSelection.politicalDivision,
          title: officeSelection.title,
          position: officeSelection.position,
          district: officeSelection.district,
          state: officeSelection.state,
          city: officeSelection.division,
          office: officeSelection.office,
          term: officeSelection.term,
        });
      });
    }

    if (Array.isArray(values.professionalExperiences)) {
      values.professionalExperiences.forEach((p) => {
        data.professionalExperiences.push({
          title: p.title,
          startDate: p.startDate,
          endDate: p.endDate,
        });
      });
    }

    if (Array.isArray(values.spouses)) {
      values.spouses.forEach((spouse) => {
        data.bio.personal.family.spouses.push({
          Name: spouse.spouse,
          childrens: spouse.noOfChildren,
        });
      });
    }

    setLoading(true);

    const accessToken = safeGetItem("access_token"); // ✅ Safe access

    return axios
      .patch(
        `${config.CANDIDATES}/candidates/update`,
        {},
        {
          headers: {
            candidate_id: props.initialData._id,
            token: accessToken,
            doc: JSON.stringify(data),
          },
        }
      )
      .then((response) => {
        if (response.status !== 200) {
          message.error(
            "Something went wrong, make sure you are connected to the internet."
          );
          return;
        }

        // ✅ safe user read
        const currentUser =
          safeJSONParse(safeGetItem("user"), storedUser) || {}; // ✅ Safe nested call

        if (currentUser?.role !== "admin") {
          const profileChanged =
            logoUrl !== props.initialData.bio.profileImage ||
            values.name !== props.initialData.bio.name;

          if (!profileChanged) {
            Modal.success({ content: "Your profile has been updated." });
            history.push("/");
            return;
          }

          const doc = JSON.stringify({
            nickname: values.name,
            picture:
              logoUrl === ""
                ? "https://s3.amazonaws.com/dev.nocap.com/uploads/logo.png"
                : logoUrl,
          });

          return axios
            .patch(
              `${config.USERS}/users`,
              {},
              {
                headers: {
                  token: accessToken, // ✅ Reuse safe token
                  doc: encodeURIComponent(doc),
                },
              }
            )
            .then((res2) => {
              if (res2.status === 200) {
                const u =
                  safeJSONParse(safeGetItem("user"), {}) || {}; // ✅ Safe nested call
                u.nickname = values.name;
                u.picture = logoUrl;
                safeSetJSON("user", u); // ✅ Safe write

                Modal.success({ content: "Your profile has been updated." });
                history.push("/");
              } else {
                message.error(
                  "Something went wrong, make sure you are connected to the internet."
                );
              }
            });
        } else {
          Modal.success({ content: "Candidate has been updated." });
          history.push("/");
        }
      })
      .catch((err) => {
        console.error(err);
        message.error("Update failed. Please try again.");
      })
      .finally(() => setLoading(false));
  };

  return (
    <Form
      name="candidateForm"
      className="accountForm"
      onFinish={onFinish}
      initialValues={{
        positions: [""],
        legislations: [""],
        contributions: [""],
        committees: [""],
      }}
      layout="vertical"
      form={form}
      onFieldsChange={() => {
        const changed = form.getFieldsValue(true);
        setFormValues(changed);
        setDisableSubmitBtn(
          JSON.stringify(changed) === JSON.stringify(initialValues)
        );
      }}
    >
      <Row gutter={[12]}>
        <Col span={24} className="candidate-edit-form-upload">
          <SingleImageUploader
            handleImageUpload={(url) => setLogoUrl(url)}
            imagePreviewUrl={logoUrl}
            typeKey={"user"}
            imageOptional={false}
            layout={"logo"}
          />
        </Col>

        <Col span={24}>
          <Form.Item
            label="Name"
            name="name"
            rules={[
              { required: true, message: "Please enter your full name" },
            ]}
          >
            <Input
              placeholder="enter full name"
              onPressEnter={(e) => (e.keyCode === 13 ? e.preventDefault() : "")}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={24} md={12}>
          <Form.Item label="Contact Information" name="contact">
            <CustomInput
              type="tel"
              placeholder="enter contact number"
              name="phone"
              rules={[
                { required: true, message: "Please enter your contact information" },
              ]}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={24} md={12}>
          <Form.Item label="Date of Birth" name="birthdate">
            <DatePicker
              style={{ width: "100%" }}
              format={dateFormatList}
              popupStyle={{ color: "black" }}
              placeholder="select a date (dd/mm/yyyy)"
            />
          </Form.Item>
        </Col>

        <Col>
          <Form.Item
            label="Profile type"
            name="profileType"
            rules={[{ required: true, message: "Please select your profile type" }]}
          >
            <Select
              showSearch
              placeholder="select profile type"
              onChange={(value) => setProfileType(value)}
              options={[
                { value: "new candidate", label: "new candidate" },
                { value: "candidate", label: "incumbent" },
                { value: "elected official", label: "elected official" },
              ]}
            />
          </Form.Item>
        </Col>

        <PoliticalParty />

        <OfficeSelectionReformed
          form={form}
          type="ballot"
          setDivisionIds={setDivisionIds}
          divisionIds={divisionIds}
        />

        <Col span={24}>
          <OfficeContacts
            setOfficeAddressQuery={setOfficeAddressQuery}
            officeAddressQuery={officeAddressQuery}
          />
        </Col>

        <Position />
        <Vote formValues={formValues} />
        <Contribution formValues={formValues} />

        <Col xs={{ span: 24 }} lg={{ span: 12 }}>
          <Form.Item label="Residence(s)">
            <Input
              suffix={<EnvironmentOutlined />}
              id="city-place-autocomplete"
              type="text"
              placeholder="search by city and state"
              onChange={(event) => setCityQuery(event.target.value)}
              value={cityQuery}
              onPressEnter={(e) => (e.keyCode === 13 ? e.preventDefault() : "")}
            />
          </Form.Item>
        </Col>

        <Religion />
        <Col span={12} />

        <Col span={24}>
          <FamilyNew formValues={formValues} />
        </Col>

        <Col span={24}>
          <Education />
        </Col>

        <Col span={24}>
          <ProfessionalExperience formValues={formValues} />
        </Col>

        <Col span={24}>
          <Committee formValues={formValues} />
        </Col>

        <Col span={24}>
          <SocialMedia />
        </Col>

        <Col span={24} style={{ textAlign: "right" }}>
          <Form.Item>
            <Button
              className="btn-bordered"
              onClick={backToAccountTypesOptions}
              style={{ margin: "0px 5px", width: "85px" }}
              type="text"
              disabled={loading}
            >
              Cancel
            </Button>

            <Button
              className="btn-solid"
              htmlType="submit"
              style={{ margin: "0px 5px", width: "85px" }}
              type="text"
              loading={loading}
              disabled={loading || disableSubmitBtn}
            >
              Save
            </Button>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default EditCandidateForm;